import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Gauge, TrendingUp, Activity } from 'lucide-react';
import { districts, sites } from '../mock';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Performance = () => {
  const performanceByDistrict = districts.map(d => ({
    ...d,
    avgPR: d.pr,
    avgCUF: d.cuf,
    sitesCount: d.sites,
  }));

  const topPerformers = [...sites].sort((a, b) => b.pr - a.pr).slice(0, 5);
  const needsAttention = [...sites].filter(s => s.pr < 60 || s.cuf < 15);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Performance Metrics</h1>
        <p className="text-base mt-2" style={{ color: '#576574' }}>PR and CUF analysis across all sites</p>
      </div>

      {/* State-wide Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gauge className="w-5 h-5" style={{ color: '#f39c12' }} />
              Performance Ratio (PR)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <div className="text-6xl font-bold mb-4" style={{ color: '#f39c12' }}>68.2%</div>
              <Progress value={68.2} className="h-3 max-w-md mx-auto mb-4" />
              <div className="grid grid-cols-3 gap-4 mt-6 text-sm">
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                  <div style={{ color: '#576574' }}>Excellent</div>
                  <div className="font-bold" style={{ color: '#10ac84' }}>{sites.filter(s => s.pr >= 75).length} sites</div>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                  <div style={{ color: '#576574' }}>Good</div>
                  <div className="font-bold" style={{ color: '#3498db' }}>{sites.filter(s => s.pr >= 60 && s.pr < 75).length} sites</div>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                  <div style={{ color: '#576574' }}>Poor</div>
                  <div className="font-bold" style={{ color: '#e74c3c' }}>{sites.filter(s => s.pr < 60).length} sites</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" style={{ color: '#9b59b6' }} />
              Capacity Utilization Factor (CUF)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <div className="text-6xl font-bold mb-4" style={{ color: '#9b59b6' }}>17.2%</div>
              <Progress value={17.2 * 5} className="h-3 max-w-md mx-auto mb-4" />
              <div className="grid grid-cols-3 gap-4 mt-6 text-sm">
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                  <div style={{ color: '#576574' }}>Excellent</div>
                  <div className="font-bold" style={{ color: '#10ac84' }}>{sites.filter(s => s.cuf >= 19).length} sites</div>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                  <div style={{ color: '#576574' }}>Good</div>
                  <div className="font-bold" style={{ color: '#3498db' }}>{sites.filter(s => s.cuf >= 15 && s.cuf < 19).length} sites</div>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                  <div style={{ color: '#576574' }}>Poor</div>
                  <div className="font-bold" style={{ color: '#e74c3c' }}>{sites.filter(s => s.cuf < 15).length} sites</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>PR Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Pie
                data={{
                  labels: ['Excellent (≥75%)', 'Good (60-74%)', 'Poor (<60%)'],
                  datasets: [{
                    data: [
                      sites.filter(s => s.pr >= 75).length,
                      sites.filter(s => s.pr >= 60 && s.pr < 75).length,
                      sites.filter(s => s.pr < 60).length
                    ],
                    backgroundColor: [
                      'rgba(16, 172, 132, 0.8)',
                      'rgba(52, 152, 219, 0.8)',
                      'rgba(231, 76, 60, 0.8)'
                    ],
                    borderColor: [
                      '#10ac84',
                      '#3498db',
                      '#e74c3c'
                    ],
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>CUF Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Pie
                data={{
                  labels: ['Excellent (≥19%)', 'Good (15-18%)', 'Poor (<15%)'],
                  datasets: [{
                    data: [
                      sites.filter(s => s.cuf >= 19).length,
                      sites.filter(s => s.cuf >= 15 && s.cuf < 19).length,
                      sites.filter(s => s.cuf < 15).length
                    ],
                    backgroundColor: [
                      'rgba(16, 172, 132, 0.8)',
                      'rgba(52, 152, 219, 0.8)',
                      'rgba(231, 76, 60, 0.8)'
                    ],
                    borderColor: [
                      '#10ac84',
                      '#3498db',
                      '#e74c3c'
                    ],
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* District Performance Chart */}
      <Card>
        <CardHeader>
          <CardTitle>District Performance Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <Bar
              data={{
                labels: districts.map(d => d.name),
                datasets: [
                  {
                    label: 'Performance Ratio (PR)',
                    data: districts.map(d => d.pr),
                    backgroundColor: 'rgba(243, 156, 18, 0.8)',
                    borderColor: '#f39c12',
                    borderWidth: 1,
                  },
                  {
                    label: 'Capacity Utilization Factor (CUF)',
                    data: districts.map(d => d.cuf),
                    backgroundColor: 'rgba(155, 89, 182, 0.8)',
                    borderColor: '#9b59b6',
                    borderWidth: 1,
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    titleColor: '#fff',
                    bodyColor: '#fff',
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                      color: '#576574',
                    }
                  },
                  x: {
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                      color: '#576574',
                    }
                  }
                }
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* District Performance */}
      <Card>
        <CardHeader>
          <CardTitle>District-wise Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ backgroundColor: '#f5f7fa' }}>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>District</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Sites</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Capacity</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Avg PR</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Avg CUF</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {performanceByDistrict.map((district) => (
                  <tr key={district.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-4 font-semibold capitalize">{district.name}</td>
                    <td className="p-4">{district.sitesCount}</td>
                    <td className="p-4">{district.capacity} MW</td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <Progress value={district.avgPR} className="h-2 w-24" />
                        <span className="font-semibold">{district.avgPR}%</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <Progress value={district.avgCUF * 5} className="h-2 w-24" />
                        <span className="font-semibold">{district.avgCUF}%</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" style={{ backgroundColor: district.status === 'good' ? '#e8f5f1' : district.status === 'moderate' ? '#fff4e6' : '#fee', color: district.status === 'good' ? '#10ac84' : district.status === 'moderate' ? '#f39c12' : '#e74c3c' }}>
                        {district.status.toUpperCase()}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" style={{ color: '#10ac84' }} />
              Top Performing Sites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topPerformers.map((site, idx) => (
                <div key={site.id} className="p-4 rounded-lg" style={{ backgroundColor: '#e8f5f1' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold" style={{ backgroundColor: '#10ac84', color: 'white' }}>
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-semibold">{site.name}</div>
                        <div className="text-sm" style={{ color: '#576574' }}>{site.id}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold" style={{ color: '#10ac84' }}>{site.pr}%</div>
                      <div className="text-xs" style={{ color: '#576574' }}>PR</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" style={{ color: '#e74c3c' }} />
              Sites Needing Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {needsAttention.map((site) => (
                <div key={site.id} className="p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-semibold">{site.name}</div>
                      <div className="text-sm" style={{ color: '#576574' }}>{site.id}</div>
                    </div>
                    <Badge variant="outline" style={{ backgroundColor: '#fee', color: '#e74c3c', borderColor: '#e74c3c' }}>
                      ACTION NEEDED
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <span style={{ color: '#576574' }}>PR: </span>
                      <span className="font-semibold" style={{ color: site.pr < 60 ? '#e74c3c' : '#576574' }}>{site.pr}%</span>
                    </div>
                    <div>
                      <span style={{ color: '#576574' }}>CUF: </span>
                      <span className="font-semibold" style={{ color: site.cuf < 15 ? '#e74c3c' : '#576574' }}>{site.cuf}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Performance;
